import { useEffect } from "react";
import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatCompactNumber } from "../../../../../../common/utils";
import ServerTable from "../../../../../../components/common/table/ServerTable";
import { useServerTableController } from "../../../../../../components/common/table/ServerTable/ServerDataGrid/useServerTableController";
import { CustomColumnMenu } from "../../../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../../../components/common/table/TableData/TableDataFooter";
import { useCustomTableDataStyles } from "../../../../../../components/common/table/TableData/useCustomTableDataStyles";
import { PageType } from "../../../../../../config/page.types";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../../../context/PageConfigrations/usePageConfigurationState";
import {
	RootCauseListPayload,
	RootCausePerimeterItemDTO,
	RootCauseWidgetPerimeterDTO,
} from "../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../../hooks/useResource";
import { mapDataColumns } from "../../../../../../utilities/dataGridUtilities";
import CustomNoRowsOverlay from "../../../../../results/ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import { CardDelta, CardDeltaProps } from "../../components/CardDelta";
import { mapRootCausePerimeterColumns, mapRootCausePerimeterRows } from "./data";

type RootCausePerimeterTableRow = RootCausePerimeterItemDTO & {
	id: number;
} & Record<string, unknown>;

type PerimeterVariationProps = {
	onWarningsChange?: (warnings?: string[]) => void;
};

export const PerimeterVariation = ({ onWarningsChange }: PerimeterVariationProps) => {
	const { t } = useTranslation("dataInsights.rootcause");
	const { rootCauseState } = useFiltersRegistry();
	const theme = useTheme();
	const tableSx = useCustomTableDataStyles();

	const [config, setConfig] = usePageConfigurationState(PageType.DataInsightsRootCauseResults, {
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},
	});

	const {
		paginationModel,
		handlePaginationChange,
		sortModel,
		handleSortModelChange,
		columnVisibilityModel,
		handleColumnVisibilityChange,
	} = useServerTableController({
		config,
		setConfig,
	});

	const { data, isLoading } = useReadResource<RootCauseListPayload, RootCauseWidgetPerimeterDTO>(
		{
			route: ApiRoutes.RootCauseWidjetPerimeter,
			query: {
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				...(sortModel?.[0]?.field && {
					sortBy: sortModel[0].field,
				}),
				...(sortModel?.[0]?.sort && {
					sortDir: sortModel[0].sort,
				}),
			},
		},
		{
			readMethod: "post",
		},
		{ ...rootCauseState.payload }
	);

	useEffect(() => {
		onWarningsChange?.(data?.warnings ?? []);
	}, [data?.warnings]);

	// Dynamic perimeter values mapped as row fields
	const perimeterRows: Record<string, unknown>[] = mapRootCausePerimeterRows(data);

	// Combines item data with perimeter values and a DataGrid id
	const rows: RootCausePerimeterTableRow[] =
		data?.items?.map((item: RootCausePerimeterItemDTO, index: number): RootCausePerimeterTableRow => {
			return {
				...item,
				id: index,
				...perimeterRows[index],
			};
		}) ?? [];

	// Static root-cause columns
	const fixedColumns = mapDataColumns<RootCausePerimeterItemDTO>(data, t, theme, [
		{
			field: "addedTkm",
			headerName: "added",
			renderAs: "number",
			cellAlign: "end",
			formatter: formatCompactNumber,
			headerMeasure: "kgCO₂e",
			sortable: true,
		},
		{
			field: "removedTkm",
			headerName: "removed",
			renderAs: "number",
			cellAlign: "end",
			formatter: formatCompactNumber,
			headerMeasure: "kgCO₂e",
			sortable: true,
		},
		{
			field: "addedCo2Kg",
			headerName: "added",
			renderAs: "number",
			cellAlign: "end",
			formatter: formatCompactNumber,
			headerMeasure: "tonne-km",
			sortable: true,
		},

		{
			field: "removedCo2Kg",
			headerName: "removed",
			renderAs: "number",
			cellAlign: "end",
			formatter: formatCompactNumber,
			headerMeasure: "tonne-km",
			sortable: true,
		},
	]);

	// Dynamic perimeter columns
	const perimeterColumns = mapRootCausePerimeterColumns(data, theme);

	// Final columns shown in the table
	const columns = [...perimeterColumns, ...fixedColumns];

	const deltaCards: CardDeltaProps[] = [
		{
			title: t("root cause results/reporting scope/co2 delta"),
			value: data?.co2Delta?.value ?? 0,
			measure: data?.co2Delta?.measure ?? "",
		},
		{
			title: t("root cause results/reporting scope/tkm delta"),
			value: data?.tkmDelta?.value ?? 0,
			measure: data?.tkmDelta?.measure ?? "",
		},
	];

	return (
		<Stack gap={2}>
			<Typography variant="body2">{t("root cause results/perimeter sub")}</Typography>

			<Stack direction="row" spacing={2}>
				{deltaCards.map((card) => (
					<CardDelta
						key={card.title}
						title={card.title}
						value={card.value}
						measure={card.measure}
						isLoading={isLoading}
					/>
				))}
			</Stack>

			<ServerTable
				isLoading={isLoading}
				getRowId={(row: RootCausePerimeterTableRow) => row.id}
				rows={rows}
				columns={columns}
				rowHeight={56}
				paginationModel={paginationModel}
				onPaginationModelChange={handlePaginationChange}
				rowCount={data?.itemsCount ?? 0}
				pageSizeOptions={[10, 25, 50]}
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={handleColumnVisibilityChange}
				slots={{
					noRowsOverlay: () => <CustomNoRowsOverlay />,
					footer: CustomDataGridFooter,
					columnMenu: CustomColumnMenu,
				}}
				sx={tableSx}
			/>
		</Stack>
	);
};
